import React, { useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import useVoyagerStore from '../../stores/useVoyagerStore';

const POPOVER_WIDTH = 360;

export default function DomainChip() {
  const domains = useVoyagerStore((s) => s.landingAgentDomains);
  const selectedId = useVoyagerStore((s) => s.selectedLandingAgentDomainId);
  const setSelected = useVoyagerStore((s) => s.setSelectedLandingAgentDomain);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);

  const selected = domains.find((d) => d.id === selectedId);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return domains;
    return domains.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (d.description || '').toLowerCase().includes(q),
    );
  }, [domains, query]);

  const open = Boolean(anchor);

  return (
    <>
      <Button
        size="small"
        variant="text"
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<FolderOpenIcon fontSize="small" />}
        endIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{
          textTransform: 'none',
          color: 'text.primary',
          borderRadius: 999,
          px: 1.25,
          py: 0.25,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
          {selected?.name || 'Select domain'}
        </Typography>
      </Button>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => { setAnchor(null); setQuery(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={0}
        slotProps={{ paper: { sx: { width: POPOVER_WIDTH, mt: 0.5, maxHeight: 340 } } }}
        onTransitionEnd={() => { if (open) searchRef.current?.focus(); }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">Domains</Typography>
        </Box>
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            inputRef={searchRef}
            size="small"
            fullWidth
            placeholder="Search domains…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto', py: 0.5 }}>
          {filtered.length === 0 && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                No matches.
              </Typography>
            </Box>
          )}
          {filtered.map((d) => {
            const isSelected = d.id === selectedId;
            return (
              <Tooltip
                key={d.id}
                title={d.description || ''}
                placement="right"
                enterDelay={400}
                disableHoverListener={!d.description}
              >
                <ListItemButton
                  selected={isSelected}
                  onClick={() => {
                    setSelected(d.id);
                    setAnchor(null);
                    setQuery('');
                  }}
                  sx={{
                    alignItems: 'flex-start',
                    borderLeft: 3,
                    borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                    '&.Mui-selected': { bgcolor: 'action.selected' },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: isSelected ? 600 : 500 }}
                      noWrap
                    >
                      {d.name}
                    </Typography>
                    {d.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {d.description}
                      </Typography>
                    )}
                  </Box>
                  {isSelected && (
                    <CheckIcon fontSize="small" color="primary" sx={{ ml: 1, mt: 0.25 }} />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Popover>
    </>
  );
}
